import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, AlertTriangle, CheckCircle } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const PsiValuesExplained = () => {
  return (
    <>
      <Helmet>
        <title>Psi Values & Thermal Bridging Explained | Junction Heat Loss | Energy Performance Direct</title>
        <meta name="description" content="Understanding Psi values and thermal bridging. What linear thermal transmittance means, why junctions matter, and how calculated Psi values improve SAP ratings." />
        <meta name="keywords" content="psi value explained, thermal bridging guide, linear thermal transmittance, junction heat loss, cold bridges, thermal bridge calculation" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/resources/psi-values-explained" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <Link to="/resources" className="text-green-600 hover:underline text-sm">← Back to Resources</Link>
              </div>
              
              <header className="mb-12">
                <span className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">Technical</span>
                <h1 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Psi Values & Thermal Bridging Explained</h1>
                <p className="text-xl text-gray-600">A guide to understanding heat loss at building junctions - what Psi values measure, why they matter, and how they affect your SAP rating.</p>
              </header>

              <section className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold mt-8 mb-4">What is a Thermal Bridge?</h2>
                <p className="text-gray-700 mb-4">
                  A thermal bridge (or cold bridge) is an area of a building envelope where heat flows more easily than through the surrounding construction. They typically occur at junctions where building elements meet - such as where a wall meets a floor, around windows, or at corners.
                </p>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 my-6">
                  <div className="flex gap-3">
                    <AlertTriangle className="h-6 w-6 text-orange-600 flex-shrink-0" />
                    <div>
                      <p className="text-orange-800 font-medium">Why Thermal Bridges Matter</p>
                      <p className="text-orange-700 mt-2">In well-insulated modern buildings, thermal bridges can account for 20-30% of total heat loss. They can also cause condensation and mould where cold surfaces meet warm, moist air.</p>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">What is a Psi (ψ) Value?</h2>
                <p className="text-gray-700 mb-4">
                  A Psi value (ψ-value, pronounced "sigh") measures the additional heat loss at a linear junction, beyond what the U-values of the connecting elements predict. It's measured in <strong>W/mK</strong> (watts per metre per Kelvin).
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 my-6">
                  <Zap className="h-8 w-8 text-blue-600 mb-3" />
                  <p className="text-blue-800 font-medium">Key Principle: Lower Psi = Less Heat Loss</p>
                  <p className="text-blue-700 mt-2">A junction with ψ = 0.05 loses half the heat of one with ψ = 0.10 per metre of junction length.</p>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Common Junction Types</h2>
                <p className="text-gray-700 mb-4">Building Regulations identify specific junction types, each with a reference code:</p>
                
                <div className="overflow-x-auto my-6">
                  <table className="w-full border rounded-lg overflow-hidden">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">Code</th>
                        <th className="px-4 py-3 text-left font-semibold">Junction Type</th>
                        <th className="px-4 py-3 text-left font-semibold">Typical ψ Range</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr><td className="px-4 py-3">E1-E5</td><td className="px-4 py-3">Wall-floor (ground floor)</td><td className="px-4 py-3">0.04-0.16</td></tr>
                      <tr className="bg-gray-50"><td className="px-4 py-3">E6-E7</td><td className="px-4 py-3">Wall-floor (intermediate)</td><td className="px-4 py-3">0.02-0.07</td></tr>
                      <tr><td className="px-4 py-3">E8-E9</td><td className="px-4 py-3">Corners</td><td className="px-4 py-3">0.02-0.09</td></tr>
                      <tr className="bg-gray-50"><td className="px-4 py-3">E10-E15</td><td className="px-4 py-3">Window/door reveals</td><td className="px-4 py-3">0.01-0.08</td></tr>
                      <tr><td className="px-4 py-3">E16-E17</td><td className="px-4 py-3">Eaves</td><td className="px-4 py-3">0.04-0.12</td></tr>
                      <tr className="bg-gray-50"><td className="px-4 py-3">E19-E24</td><td className="px-4 py-3">Party wall junctions</td><td className="px-4 py-3">0.02-0.12</td></tr>
                    </tbody>
                  </table>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Default vs Calculated Psi Values</h2>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Card className="border-red-200">
                    <CardContent className="pt-6">
                      <h3 className="font-bold text-lg mb-3 text-red-700">Using Default Values</h3>
                      <ul className="space-y-2 text-gray-600 text-sm">
                        <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-1" /> Conservative assumptions</li>
                        <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-1" /> Penalises SAP rating</li>
                        <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-1" /> May need compensating measures</li>
                        <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-1" /> Doesn't reflect actual design</li>
                      </ul>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200">
                    <CardContent className="pt-6">
                      <h3 className="font-bold text-lg mb-3 text-green-700">Using Calculated Values</h3>
                      <ul className="space-y-2 text-gray-600 text-sm">
                        <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-1" /> Reflects actual details</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-1" /> Typically improves SAP by 2-5 pts</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-1" /> Can unlock compliance</li>
                        <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-1" /> Demonstrates design quality</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">How Psi Values Affect Your SAP Rating</h2>
                <p className="text-gray-700 mb-4">
                  SAP uses a parameter called the "y-value" to account for thermal bridging. This is calculated from all the Psi values and junction lengths in your building. Better (lower) Psi values directly improve your SAP rating.
                </p>
                <p className="text-gray-700 mb-4">
                  For a typical new build house, moving from default Psi values to calculated values can improve the SAP rating by 2-5 points - often the difference between meeting Part L or not.
                </p>

                <h2 className="text-2xl font-bold mt-8 mb-4">Related Services</h2>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Link to="/psi-value-calculations" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">Psi Value Calculations</h3>
                        <p className="text-gray-600 text-sm">Professional thermal bridging analysis</p>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/resources/u-value-examples" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">U-Values Guide</h3>
                        <p className="text-gray-600 text-sm">Understanding element U-values</p>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </section>

              <div className="mt-12 p-6 bg-green-50 rounded-lg text-center">
                <h3 className="text-xl font-bold mb-3">Need Psi Value Calculations?</h3>
                <p className="text-gray-600 mb-4">Improve your SAP rating with calculated junction values.</p>
                <Link to="/booking">
                  <Button className="bg-green-600 hover:bg-green-700">
                    Request Quote <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default PsiValuesExplained;
